'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiHeart, FiTarget, FiUsers, FiX, FiTrendingUp,
  FiSearch, FiShare2, FiCheckCircle, FiStar, FiClock,
  FiChevronLeft, FiChevronRight, FiAward, FiCopy,
  FiBookOpen, FiAlertTriangle, FiGlobe, FiBriefcase, FiMusic,
  FiPlay, FiVideo
} from 'react-icons/fi';
import { FaFacebook, FaWhatsapp, FaDog, FaChurch, FaRunning, FaPlane, FaTwitter } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import donationsService, { Campaign, Category, Donation } from '../services/donations.service';
import { walletService, servicesService, paymentService } from '../services';
import authService from '../services/auth.service';
import type { WalletCard } from '../types';
import { Card, LoaderComponent, EmptyState } from '../components/ui';
import CampanhaDestaqueCTA from '../components/CampanhaDestaqueCTA';
import { fmtParede } from '../utils/tempoCv';
import { WALLET_PAY_ENABLED } from '../config/wallet-pay';

// ─── Helpers ────────────────────────────────────────────────────────────────

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  medical_services: <FiHeart />, school: <FiBookOpen />, warning: <FiAlertTriangle />,
  favorite: <FiHeart />, pets: <FaDog />, groups: <FiUsers />,
  sports_soccer: <FaRunning />, palette: <FiMusic />, business: <FiBriefcase />,
  flight: <FaPlane />, church: <FaChurch />, more_horiz: <FiGlobe />,
};
function getCategoryIcon(name?: string) { return CATEGORY_ICONS[name || ''] || <FiHeart />; }

function getVideoEmbedUrl(url?: string | null): string | null {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/\s]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return null;
}

function parseSuggestedAmounts(raw?: string | null): number[] {
  if (!raw) return [500, 1000, 2500, 5000, 10000];
  const parsed = raw.split(',').map(v => parseInt(v.trim(), 10)).filter(n => !isNaN(n) && n > 0);
  return parsed.length ? parsed : [500, 1000, 2500, 5000, 10000];
}

// ─── Sub-components ──────────────────────────────────────────────────────────

const ProgressBar: React.FC<{ progress: number; size?: 'sm' | 'lg' }> = ({ progress, size = 'sm' }) => (
  <div className={`w-full ${size === 'lg' ? 'h-3' : 'h-1.5'} bg-gray-100 rounded-full overflow-hidden`}>
    <div
      className="h-full rounded-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all duration-700"
      style={{ width: `${Math.min(100, progress)}%` }}
    />
  </div>
);

const Badge: React.FC<{ type: 'verified' | 'featured' | 'trending' }> = ({ type }) => {
  const cfg = {
    verified: { cls: 'bg-emerald-500/90 text-white', icon: <FiCheckCircle className="w-3 h-3" />, label: 'Verificada' },
    featured:  { cls: 'bg-amber-500/90 text-white',   icon: <FiStar className="w-3 h-3" />,        label: 'Destaque' },
    trending:  { cls: 'bg-rose-500/90 text-white',    icon: <FiTrendingUp className="w-3 h-3" />,   label: 'Trending' },
  }[type];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold backdrop-blur-sm ${cfg.cls}`}>
      {cfg.icon}{cfg.label}
    </span>
  );
};

const CategoryChip: React.FC<{ category: Category; selected: boolean; onClick: () => void }> = ({ category, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer
      ${selected
        ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md shadow-pink-500/25'
        : 'bg-white text-gray-600 border border-gray-200 hover:border-pink-300 hover:text-pink-500'
      }`}
  >
    <span className={`text-base ${selected ? 'text-white' : 'text-pink-400'}`}>{getCategoryIcon(category.ICONE)}</span>
    {category.NOME}
    {(category.totalCampanhas ?? 0) > 0 && (
      <span className={`text-xs px-1.5 py-0.5 rounded-full ${selected ? 'bg-white/25' : 'bg-gray-100 text-gray-400'}`}>
        {category.totalCampanhas}
      </span>
    )}
  </button>
);

const CampaignCard: React.FC<{ campaign: Campaign; onClick: () => void; featured?: boolean }> = ({ campaign, onClick, featured }) => {
  const progress = donationsService.getDonationProgress(campaign);
  const embedUrl = getVideoEmbedUrl(campaign.VIDEO_URL);
  return (
    <button
      onClick={onClick}
      className={`group w-full text-left bg-white rounded-2xl overflow-hidden border border-gray-100
                 hover:shadow-xl hover:shadow-pink-500/10 hover:-translate-y-1 transition-all duration-300 cursor-pointer
                 ${featured ? 'md:col-span-2' : ''}`}
    >
      {/* Image / Video thumbnail */}
      <div className={`relative ${featured ? 'aspect-[16/7]' : 'aspect-video'} bg-gradient-to-br from-pink-400 to-rose-500 overflow-hidden`}>
        {(campaign.IMAGEM_CAPA || campaign.IMAGEM) ? (
          <img
            src={campaign.IMAGEM_CAPA || campaign.IMAGEM || ''}
            alt={campaign.TITULO}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FiHeart className="text-5xl text-white/40" />
          </div>
        )}

        {/* Gradient overlay — stronger at bottom for title readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

        {/* Badges top-left */}
        <div className="absolute top-3 left-3 flex gap-1.5">
          {campaign.VERIFICADA && <Badge type="verified" />}
          {campaign.DESTAQUE && <Badge type="featured" />}
          {campaign.TRENDING && <Badge type="trending" />}
        </div>

        {/* Video indicator top-right */}
        {embedUrl && (
          <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
            <FiVideo className="w-3 h-3" /> Vídeo
          </div>
        )}

        {/* Title overlay — pill with blur backdrop for any image */}
        <div className="absolute bottom-0 left-0 right-0 p-3">
          {(campaign.CATEGORIA_NOME || campaign.categoriaNome) && (
            <span className="inline-block mb-1.5 px-2 py-0.5 bg-white/15 text-white/90 text-xs rounded-full backdrop-blur-sm border border-white/20">
              {campaign.CATEGORIA_NOME || campaign.categoriaNome}
            </span>
          )}
          <div className="inline-block w-full">
            <h3 className={`font-bold text-white leading-snug line-clamp-2
              drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]
              ${featured ? 'text-xl' : 'text-sm'}`}>
              {campaign.TITULO}
            </h3>
          </div>
        </div>
      </div>

      {/* Card body — só progresso, sem descrição */}
      <div className="px-4 py-3">
        {/* Progress */}
        {(Number(campaign.META_VALOR || 0)) > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-bold text-pink-500">
                {donationsService.formatCurrency(Number(campaign.VALOR_ARRECADADO || 0))}
              </span>
              <span className="text-gray-400 text-xs">
                de {donationsService.formatCurrency(Number(campaign.META_VALOR || 0))}
              </span>
            </div>
            <ProgressBar progress={progress} />
            <div className="flex items-center justify-between text-xs text-gray-400">
              <span className="flex items-center gap-1"><FiUsers className="w-3 h-3 text-pink-400" /> {campaign.TOTAL_DOADORES || 0} doadores</span>
              <span className="font-medium text-pink-500">
                {progress > 0
                  ? donationsService.formatProgress((campaign as any).PERCENTAGEM_ALCANCADA ?? progress)
                  : `Faltam ${donationsService.formatCurrency(Number((campaign as any).VALOR_EM_FALTA ?? campaign.META_VALOR ?? 0))}`}
              </span>
            </div>
          </div>
        )}
      </div>
    </button>
  );
};

const DonorItem: React.FC<{ donation: Donation; rank?: number }> = ({ donation, rank }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-gray-50 last:border-0">
    {rank && (
      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0
        ${rank === 1 ? 'bg-amber-100 text-amber-700' : rank === 2 ? 'bg-gray-100 text-gray-600' : rank === 3 ? 'bg-orange-100 text-orange-700' : 'bg-gray-50 text-gray-400'}`}>
        {rank}
      </div>
    )}
    <div className="w-8 h-8 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center shrink-0">
      <span className="text-white text-xs font-semibold">
        {donation.ANONIMO ? '?' : (donation.doadorNome || 'A').charAt(0).toUpperCase()}
      </span>
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-gray-800 truncate">
        {donation.ANONIMO ? 'Doador Anónimo' : donation.doadorNome}
      </p>
      {donation.MENSAGEM && (
        <p className="text-xs text-gray-400 truncate italic">"{donation.MENSAGEM}"</p>
      )}
    </div>
    <div className="text-right shrink-0">
      <p className="text-sm font-bold text-pink-500">{donationsService.formatCurrency(donation.VALOR)}</p>
      <p className="text-xs text-gray-400">{donationsService.formatRelativeTime(donation.DATA_DONATIVO)}</p>
    </div>
  </div>
);

// ─── Campaign Detail Modal ───────────────────────────────────────────────────

const CampaignDetailModal: React.FC<{
  campaign: Campaign | null;
  isOpen: boolean;
  walletCards: WalletCard[];
  onClose: () => void;
  onDonate: (campaignId: string, amount: number, walletCardId?: string, donorData?: { nome: string; email: string; telefone: string; anonimo: boolean }) => void;
}> = ({ campaign, isOpen, walletCards, onClose, onDonate }) => {
  const [amount, setAmount] = useState('');
  const [selectedWallet, setSelectedWallet] = useState('sisp'); // 'sisp' | '' (wallet) | card.id (saved card)
  const [customAmount, setCustomAmount] = useState(false);
  const [activeTab, setActiveTab] = useState<'about' | 'donors' | 'updates' | 'events'>('about');
  const [copied, setCopied] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // Donor info — required even if anonymous
  const [donorNome, setDonorNome] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorTelefone, setDonorTelefone] = useState('');
  const [anonimo, setAnonimo] = useState(false);
  const [donorErrors, setDonorErrors] = useState<{ nome?: string; email?: string; telefone?: string }>({});

  // Pre-fill from session on mount
  React.useEffect(() => {
    const session = authService.getCurrentSession();
    if (session) {
      setDonorNome(session.cliente?.NOME || session.user?.name || '');
      setDonorEmail(session.cliente?.EMAIL || session.user?.email || '');
      setDonorTelefone(session.cliente?.TELEMOVEL || '');
    }
  }, []);

  if (!isOpen || !campaign) return null;

  const progress = donationsService.getDonationProgress(campaign);
  const embedUrl = getVideoEmbedUrl(campaign.VIDEO_URL);
  const suggestedAmounts = parseSuggestedAmounts((campaign as any).MONTANTES_SUGERIDOS);
  const shareUrl = `${window.location.origin}/doacoes/${campaign.ID}`;

  const handleShare = (platform: string) => {
    const text = `Ajude esta causa: ${campaign.TITULO}`;
    const urls: Record<string, string> = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`,
    };
    if (urls[platform]) window.open(urls[platform], '_blank', 'width=600,height=400');
    donationsService.shareCampaign(campaign.ID, platform.toUpperCase()).catch(() => {});
    setShowShare(false);
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(shareUrl).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 2000);
    donationsService.shareCampaign(campaign.ID, 'LINK').catch(() => {});
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-3xl overflow-hidden shadow-2xl max-h-screen sm:max-h-[92vh] flex flex-col">
        {/* Hero: image or video */}
        <div className="relative shrink-0 overflow-hidden rounded-t-3xl">
          {embedUrl ? (
            <div className="aspect-video bg-black">
              <iframe src={embedUrl} className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen title={campaign.TITULO} />
            </div>
          ) : (
            <div className="h-52 bg-gradient-to-br from-pink-400 to-rose-500 relative overflow-hidden">
              {(campaign.IMAGEM_CAPA || campaign.IMAGEM) && (
                <img src={campaign.IMAGEM_CAPA || campaign.IMAGEM || ''} alt={campaign.TITULO} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
          )}

          {/* Badges */}
          <div className="absolute top-3 left-3 flex gap-1.5">
            {campaign.VERIFICADA && <Badge type="verified" />}
            {campaign.DESTAQUE && <Badge type="featured" />}
            {campaign.TRENDING && <Badge type="trending" />}
          </div>

          {/* Actions top-right */}
          <div className="absolute top-3 right-3 flex gap-2">
            <div className="relative">
              <button onClick={() => setShowShare(!showShare)} className="p-2 bg-white/90 hover:bg-white rounded-full transition-colors cursor-pointer shadow-sm">
                <FiShare2 className="w-4 h-4 text-gray-700" />
              </button>
              {showShare && (
                <div className="absolute right-0 mt-2 w-44 bg-white rounded-2xl shadow-xl border border-gray-100 py-1 z-10">
                  {[
                    { key: 'facebook', icon: <FaFacebook className="text-blue-600" />, label: 'Facebook' },
                    { key: 'twitter',  icon: <FaTwitter className="text-sky-500" />,   label: 'Twitter' },
                    { key: 'whatsapp', icon: <FaWhatsapp className="text-green-500" />, label: 'WhatsApp' },
                  ].map(s => (
                    <button key={s.key} onClick={() => handleShare(s.key)}
                      className="w-full px-4 py-2.5 text-left flex items-center gap-3 text-sm hover:bg-gray-50 transition-colors cursor-pointer">
                      {s.icon} {s.label}
                    </button>
                  ))}
                  <hr className="my-1 border-gray-100" />
                  <button onClick={handleCopyLink}
                    className="w-full px-4 py-2.5 text-left flex items-center gap-3 text-sm hover:bg-gray-50 transition-colors cursor-pointer">
                    <FiCopy className="text-gray-500" /> {copied ? '✓ Copiado!' : 'Copiar link'}
                  </button>
                </div>
              )}
            </div>
            <button onClick={onClose} className="p-2 bg-white/90 hover:bg-white rounded-full transition-colors cursor-pointer shadow-sm">
              <FiX className="w-4 h-4 text-gray-700" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="overflow-y-auto flex-1">
          <div className="p-5">
            {/* Title */}
            <div className="mb-4">
              {campaign.ORGANIZADOR_NOME && (
                <p className="text-xs text-gray-400 mb-1">
                  Organizado por <span className="font-medium text-gray-600">{campaign.ORGANIZADOR_NOME}</span>
                  {(campaign as any).entidadeNome && ` · ${(campaign as any).entidadeNome}`}
                </p>
              )}
              <h2 className="text-xl font-bold text-gray-900 leading-snug">{campaign.TITULO}</h2>
              {(campaign.CATEGORIA_NOME || campaign.categoriaNome) && (
                <span className="inline-block mt-2 px-3 py-1 bg-pink-50 text-pink-600 text-xs font-medium rounded-full">
                  {campaign.CATEGORIA_NOME || campaign.categoriaNome}
                </span>
              )}
            </div>

            {/* Progress card */}
            {Number(campaign.META_VALOR || 0) > 0 && (
              <div className="bg-white rounded-2xl p-4 mb-5 border border-gray-100 shadow-sm">
                <div className="flex items-end justify-between mb-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Total arrecadado</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {donationsService.formatCurrency(Number(campaign.VALOR_ARRECADADO || 0))}
                    </p>
                    <p className="text-xs text-gray-400">de {donationsService.formatCurrency(Number(campaign.META_VALOR || 0))}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400 mb-0.5">Apoiantes</p>
                    <p className="text-2xl font-bold text-gray-900">{campaign.TOTAL_DOADORES || 0}</p>
                    <p className="text-xs text-gray-400">doadores</p>
                  </div>
                </div>
                <ProgressBar progress={progress} size="lg" />
                <div className="flex justify-between mt-1.5">
                  <span className="text-xs text-gray-400">{donationsService.formatProgress(progress)} alcançado</span>
                  {progress < 100 ? (
                    <span className="text-xs font-semibold text-rose-500">
                      Faltam {donationsService.formatCurrency(Number((campaign as any).VALOR_EM_FALTA ?? Math.max(0, Number(campaign.META_VALOR || 0) - Number(campaign.VALOR_ARRECADADO || 0))))}
                    </span>
                  ) : (
                    <span className="text-xs font-semibold text-emerald-600">🎉 Meta atingida!</span>
                  )}
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 bg-gray-50 p-1 rounded-xl mb-4 overflow-x-auto">
              {[
                { key: 'about',   label: 'Sobre', count: null },
                { key: 'donors',  label: 'Doadores', count: campaign.TOTAL_DOADORES || 0 },
                { key: 'updates', label: 'Updates', count: (campaign.updates || campaign.atualizacoes || []).length },
                ...((campaign as any).EVENTO_ID ? [{ key: 'events', label: '🎟 Eventos', count: null }] : []),
              ].map(({ key, label, count }) => (
                <button key={key} onClick={() => setActiveTab(key as any)}
                  className={`flex-shrink-0 flex-1 py-2 px-2 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap
                    ${activeTab === key ? 'bg-white text-pink-500 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}>
                  {label}{count !== null ? ` (${count})` : ''}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="mb-5 min-h-[100px]">
              {activeTab === 'about' && (() => {
                const texto = campaign.HISTORIA || campaign.DESCRICAO || '';
                const paragrafos = texto.split(/\n\n|\n(?=[A-ZÁÉÍÓÚÀÂÃÊÔÇ])/).filter((p: string) => p.trim());
                return (
                  <div className="space-y-5">
                    {paragrafos.length > 0 ? (
                      <>
                        {/* Primeiro parágrafo — destaque editorial */}
                        <div className="relative pl-4 border-l-4 border-gradient-to-b border-pink-400">
                          <p className="text-base text-gray-800 leading-relaxed font-medium"
                            style={{ borderLeft: '4px solid #f43f5e' }}>
                            <span className="float-left text-6xl font-black text-pink-500 leading-none mr-1 mt-1"
                              style={{ fontFamily: 'Georgia, serif', lineHeight: '0.8' }}>
                              {paragrafos[0].trim().charAt(0)}
                            </span>
                            {paragrafos[0].trim().slice(1)}
                          </p>
                        </div>

                        {/* Restantes parágrafos */}
                        {paragrafos.slice(1).map((para: string, i: number) => (
                          <p key={i} className="text-sm text-gray-600 leading-[1.8] tracking-wide">
                            {para.trim()}
                          </p>
                        ))}
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <FiBookOpen className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                        <p className="text-sm text-gray-400 italic">Sem descrição disponível.</p>
                      </div>
                    )}

                    {/* Beneficiário */}
                    {campaign.BENEFICIARIO_NOME && (
                      <div className="flex items-start gap-3 bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-4 border border-pink-100">
                        <div className="w-8 h-8 rounded-full bg-pink-200 flex items-center justify-center shrink-0">
                          <FiHeart className="w-4 h-4 text-pink-600" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-pink-600 uppercase tracking-wider mb-0.5">Beneficiário</p>
                          <p className="text-sm font-semibold text-gray-800">{campaign.BENEFICIARIO_NOME}</p>
                        </div>
                      </div>
                    )}

                    {/* Recompensas */}
                    {(campaign.rewards || campaign.recompensas || []).length > 0 && (
                      <div>
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                          <FiAward className="text-amber-500 w-4 h-4" /> Recompensas por Doação
                        </h4>
                        <div className="space-y-2">
                          {(campaign.rewards || campaign.recompensas || []).map(r => (
                            <div key={r.ID} className="group flex justify-between items-center p-3.5 bg-white rounded-2xl border border-gray-100 hover:border-pink-200 hover:shadow-sm transition-all">
                              <div>
                                <p className="text-sm font-semibold text-gray-800">{r.TITULO}</p>
                                {r.DESCRICAO && <p className="text-xs text-gray-400 mt-0.5">{r.DESCRICAO}</p>}
                              </div>
                              <span className="ml-3 px-3 py-1 bg-pink-50 text-pink-600 font-bold text-xs rounded-full border border-pink-100">
                                {donationsService.formatCurrency(r.VALOR_MINIMO)}+
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {activeTab === 'donors' && (
                <div>
                  {(campaign.recentDonations || campaign.topDoadores || []).length > 0 ? (
                    <div>
                      {(campaign.recentDonations || campaign.topDoadores || []).slice(0, 3).map((d, i) => (
                        <DonorItem key={i} donation={d} rank={i + 1} />
                      ))}
                      {(campaign.recentDonations || []).length > 3 && (campaign.recentDonations || []).slice(3).map(d => (
                        <DonorItem key={d.ID} donation={d} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 italic text-center py-6">Seja o primeiro a doar!</p>
                  )}
                </div>
              )}

              {activeTab === 'updates' && (
                <div className="space-y-3">
                  {(campaign.updates || campaign.atualizacoes || []).length > 0 ? (
                    (campaign.updates || campaign.atualizacoes || []).map(u => (
                      <div key={u.ID} className="border-l-2 border-pink-200 pl-4 py-1">
                        <p className="text-xs text-gray-400 mb-1">{donationsService.formatDate(u.DATA_CRIACAO)}</p>
                        <h4 className="text-sm font-semibold text-gray-800">{u.TITULO}</h4>
                        <p className="text-sm text-gray-500 mt-0.5">{u.CONTEUDO}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-400 italic text-center py-6">Sem actualizações ainda.</p>
                  )}
                </div>
              )}

              {activeTab === 'events' && (() => {
                const ev = campaign as any;
                if (!ev.EVENTO_ID && !ev.EVENTO_TITULO) {
                  return <p className="text-sm text-gray-400 italic text-center py-6">Nenhum evento associado a esta campanha.</p>;
                }
                const statusBadge: Record<string, { label: string; cls: string }> = {
                  PUBLISHED: { label: 'Publicado', cls: 'bg-emerald-100 text-emerald-700' },
                  DRAFT:     { label: 'Rascunho', cls: 'bg-gray-100 text-gray-600' },
                  CANCELLED: { label: 'Cancelado', cls: 'bg-red-100 text-red-600' },
                  COMPLETED: { label: 'Concluído', cls: 'bg-blue-100 text-blue-600' },
                  POSTPONED: { label: 'Adiado',    cls: 'bg-amber-100 text-amber-600' },
                };
                const status = statusBadge[ev.EVENTO_STATUS] || { label: ev.EVENTO_STATUS || '', cls: 'bg-gray-100 text-gray-600' };
                const dataInicio = ev.EVENTO_DATA_INICIO ? new Date(ev.EVENTO_DATA_INICIO) : null;
                return (
                  <div className="space-y-4">
                    {/* Evento card */}
                    <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
                      {ev.EVENTO_IMAGEM && (
                        <img src={ev.EVENTO_IMAGEM} alt={ev.EVENTO_TITULO} className="w-full h-36 object-cover" />
                      )}
                      {!ev.EVENTO_IMAGEM && (
                        <div className="w-full h-24 bg-gradient-to-br from-violet-400 to-purple-600 flex items-center justify-center">
                          <FiPlay className="w-10 h-10 text-white/50" />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h3 className="font-bold text-gray-900 text-base leading-snug">{ev.EVENTO_TITULO}</h3>
                          {ev.EVENTO_STATUS && (
                            <span className={`shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${status.cls}`}>{status.label}</span>
                          )}
                        </div>
                        {dataInicio && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                            <FiClock className="w-3 h-3 text-violet-400" />
                            {fmtParede(dataInicio, { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }, 'pt-PT')}
                          </div>
                        )}
                        {(ev.EVENTO_LOCAL || ev.EVENTO_CIDADE) && (
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="text-violet-400">📍</span>
                            {[ev.EVENTO_LOCAL, ev.EVENTO_CIDADE].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* CTA bilhetes */}
                    {ev.EVENTO_STATUS === 'PUBLISHED' && (
                      <a
                        href={ev.EVENTO_SLUG ? `https://events.cv/e/${ev.EVENTO_SLUG}` : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl
                          bg-gradient-to-r from-violet-500 to-purple-600 text-white font-semibold text-sm
                          hover:from-violet-600 hover:to-purple-700 transition-all shadow-md shadow-violet-500/25"
                      >
                        <FiPlay className="w-4 h-4" /> Comprar Bilhetes para este Evento
                      </a>
                    )}
                    <p className="text-xs text-center text-gray-400">
                      Este evento está associado a esta campanha de crowdfunding.
                    </p>
                  </div>
                );
              })()}
            </div>

            {/* Donation section */}
            <div className="border-t border-gray-100 pt-5">
              <h3 className="text-sm font-bold text-gray-800 mb-3">Fazer Donativo</h3>

              {/* Suggested amounts — from campaign config */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                {suggestedAmounts.map(v => (
                  <button key={v} onClick={() => { setAmount(String(v)); setCustomAmount(false); }}
                    className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-all cursor-pointer
                      ${amount === String(v) && !customAmount
                        ? 'border-pink-500 bg-pink-50 text-pink-600'
                        : 'border-gray-200 text-gray-600 hover:border-pink-300'}`}>
                    {donationsService.formatCurrency(v)}
                  </button>
                ))}
                <button onClick={() => { setAmount(''); setCustomAmount(true); }}
                  className={`py-2.5 rounded-xl text-sm font-semibold border-2 transition-all cursor-pointer
                    ${customAmount ? 'border-pink-500 bg-pink-50 text-pink-600' : 'border-gray-200 text-gray-600 hover:border-pink-300'}`}>
                  Outro
                </button>
              </div>

              {customAmount && (
                <div className="mb-3 relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">CVE</span>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                    placeholder="0" min="1" autoFocus
                    className="w-full pl-14 pr-4 py-3 border-2 border-pink-200 rounded-xl text-lg font-semibold focus:outline-none focus:border-pink-500 transition-colors" />
                </div>
              )}

              {/* Dados do doador — obrigatórios mesmo que anónimo */}
              <div className="mb-4 space-y-2.5">
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Dados do Doador <span className="text-rose-500 font-normal normal-case tracking-normal">(obrigatório)</span>
                </p>
                <div>
                  <input type="text" value={donorNome}
                    onChange={e => { setDonorNome(e.target.value); setDonorErrors(p => ({ ...p, nome: undefined })); }}
                    placeholder="Nome completo *"
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 transition-colors
                      ${donorErrors.nome ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                  {donorErrors.nome && <p className="text-xs text-rose-500 mt-0.5 pl-1">{donorErrors.nome}</p>}
                </div>
                <div>
                  <input type="email" value={donorEmail}
                    onChange={e => { setDonorEmail(e.target.value); setDonorErrors(p => ({ ...p, email: undefined })); }}
                    placeholder="Email *"
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 transition-colors
                      ${donorErrors.email ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                  {donorErrors.email && <p className="text-xs text-rose-500 mt-0.5 pl-1">{donorErrors.email}</p>}
                </div>
                <div>
                  <input type="tel" value={donorTelefone}
                    onChange={e => { setDonorTelefone(e.target.value); setDonorErrors(p => ({ ...p, telefone: undefined })); }}
                    placeholder="Telefone *"
                    className={`w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 transition-colors
                      ${donorErrors.telefone ? 'border-rose-400 bg-rose-50' : 'border-gray-200'}`} />
                  {donorErrors.telefone && <p className="text-xs text-rose-500 mt-0.5 pl-1">{donorErrors.telefone}</p>}
                </div>
                <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
                  <input type="checkbox" checked={anonimo} onChange={e => setAnonimo(e.target.checked)}
                    className="w-4 h-4 accent-pink-500 rounded cursor-pointer" />
                  <span className="text-xs text-gray-500">
                    Doação anónima <span className="text-gray-400">(nome não exibido publicamente)</span>
                  </span>
                </label>
              </div>

              {/* Método de pagamento */}
              <div className="mb-3 space-y-1.5">
                <p className="text-xs text-gray-400 mb-2">Método de pagamento</p>

                {/* Sempre visível: Cartão (abre webview SISP para introduzir dados) */}
                <button
                  onClick={() => setSelectedWallet('sisp')}
                  className={`w-full p-3 rounded-xl border-2 text-left transition-all text-sm cursor-pointer flex items-center gap-3
                    ${selectedWallet === 'sisp' ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <span className="text-lg">💳</span>
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">Cartão Vinti4 / Internacional</p>
                    <p className="text-xs text-gray-400">Inserir dados do cartão no terminal SISP</p>
                  </div>
                </button>

                {/* eCard — token SISP guardado (só se existir) */}
                {walletCards.filter(c => c.token).map(card => (
                  <button key={card.ID || card.id}
                    onClick={() => setSelectedWallet(card.ID || card.id || '')}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all text-sm cursor-pointer flex items-center gap-3
                      ${selectedWallet === (card.ID || card.id) ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <span className="text-lg">💳</span>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">eCard — Pagamento Rápido</p>
                      <p className="text-xs text-gray-400">Cartão guardado **** {card.NUMERO?.slice(-4) || '****'} · {card.designacao || 'Vinti4'}</p>
                    </div>
                  </button>
                ))}

                {/* Saldo Pagali — o backend passou a ligar o pagamento por saldo
                    ao donativo e a confirmá-lo (serviço donativo-pago). Antes
                    ficava de fora porque o saldo saía e o donativo não era
                    confirmado. Só aparece com saldo disponível. */}
                {WALLET_PAY_ENABLED && walletCards.filter(c => !c.token && Number(c.saldo || 0) > 0).map(w => (
                  <button key="saldo-pagali"
                    onClick={() => setSelectedWallet('wallet')}
                    className={`w-full p-3 rounded-xl border-2 text-left transition-all text-sm cursor-pointer flex items-center gap-3
                      ${selectedWallet === 'wallet' ? 'border-pink-500 bg-pink-50' : 'border-gray-200 hover:border-gray-300'}`}>
                    <span className="text-lg">👛</span>
                    <div>
                      <p className="font-semibold text-gray-800 text-sm">Saldo Pagali</p>
                      <p className="text-xs text-gray-400">{w.designacao}</p>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => {
                  const n = parseFloat(amount);
                  if (isNaN(n) || n <= 0) return;
                  const errors: { nome?: string; email?: string; telefone?: string } = {};
                  if (!donorNome.trim()) errors.nome = 'Nome obrigatório';
                  if (!donorEmail.trim() || !/\S+@\S+\.\S+/.test(donorEmail)) errors.email = 'Email válido obrigatório';
                  if (!donorTelefone.trim()) errors.telefone = 'Telefone obrigatório';
                  if (Object.keys(errors).length > 0) { setDonorErrors(errors); return; }
                  onDonate(campaign.ID, n, selectedWallet || undefined, {
                    nome: donorNome.trim(), email: donorEmail.trim(),
                    telefone: donorTelefone.trim(), anonimo,
                  });
                }}
                disabled={!amount || parseFloat(amount) <= 0}
                className="w-full py-4 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600
                           text-white font-bold rounded-2xl flex items-center justify-center gap-2 transition-all
                           disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-pink-500/25 cursor-pointer">
                <FiHeart className="w-4 h-4" />
                {amount && parseFloat(amount) > 0
                  ? `Doar ${donationsService.formatCurrency(parseFloat(amount))}`
                  : 'Selecione um valor'}
              </button>
              <p className="text-xs text-center text-gray-400 mt-3">
                O seu donativo faz a diferença. Obrigado pela sua generosidade! 🙏
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────────────────────

const Doacoes: React.FC = () => {
  const { t } = useTranslation();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [featuredCampaigns, setFeaturedCampaigns] = useState<Campaign[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [walletCards, setWalletCards] = useState<WalletCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [partners, setPartners] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    sessionStorage.setItem('pageTitle', 'Doações');
    loadInitialData();
    servicesService.getServicesByCategory('8').then(setPartners).catch(() => {});

    // Quando o utilizador volta ao tab/janela (ex: depois do popup SISP fechar),
    // recarregar campanhas para reflectir doações confirmadas
    const handleFocus = () => { loadCampaigns(); donationsService.getFeaturedCampaigns().then(setFeaturedCampaigns).catch(() => {}); };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  useEffect(() => { loadCampaigns(); }, [searchQuery, selectedCategory, page]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [cats, featured] = await Promise.all([donationsService.getCategories(), donationsService.getFeaturedCampaigns()]);
      setCategories(cats);
      setFeaturedCampaigns(featured);
      await Promise.all([loadCampaigns(), loadWalletCards()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    try {
      const res = await donationsService.getCampaigns({ search: searchQuery || undefined, categoria: selectedCategory || undefined, page, limit: 12 });
      setCampaigns(res.data || []);
      setTotalPages(res.pagination?.totalPages || 1);
    } catch (err) { console.error(err); }
  };

  const loadWalletCards = async () => {
    try {
      const cards: WalletCard[] = [];
      const wallet = await walletService.getWallet();
      if (wallet && wallet.saldo_disponivel > 0) {
        cards.push({ id: String(wallet.id), token: '', corTema: '#6233A0', descricao: 'Pagali Wallet', DESIG: 'Pagali Wallet', designacao: `Saldo: ${wallet.saldo_disponivel.toFixed(0)}$00 CVE`, saldo: wallet.saldo_disponivel, pan: '' });
      }
      try {
        const saved = await walletService.getCards();
        for (const card of saved) {
          cards.push({ id: card.id, token: card.token, corTema: '#6233A0', descricao: 'Pagali eCard', DESIG: 'Pagali eCard', designacao: card.cardType || 'Vinti4', NUMERO: `****${card.lastFourDigits}`, pan: `****${card.lastFourDigits}` });
        }
      } catch { /* ignore */ }
      setWalletCards(cards);
    } catch { /* ignore */ }
  };

  const handleCampaignClick = async (campaign: Campaign) => {
    try {
      const full = await donationsService.getCampaignDetails(campaign.ID);
      setSelectedCampaign(full);
      setModalOpen(true);
    } catch { setError(t('common.error')); }
  };

  const handleDonate = async (campaignId: string, amount: number, walletCardId?: string, donorData?: { nome: string; email: string; telefone: string; anonimo: boolean }) => {
    try {
      // 1. Register donation as PENDING — dados do formulário obrigatório
      const res = await donationsService.createDonation({
        campanha_id: campaignId,
        valor: amount,
        doador_nome: donorData?.nome || undefined,
        doador_email: donorData?.email || undefined,
        doador_telefone: donorData?.telefone || undefined,
        anonimo: donorData?.anonimo || false,
      });

      if (!res?.id) throw new Error('Erro ao registar doação');

      // 2a. Cartão guardado (eCard): cobra-se o token e NÃO se abre a SISP.
      //
      // O botão «eCard — Pagamento Rápido» existia e era decorativo: o cartão
      // escolhido chegava aqui como `_walletCardId` e era ignorado, pelo que
      // toda a gente acabava na página da SISP a escrever os dados do cartão —
      // que é precisamente o que um cartão guardado existe para evitar.
      // O caminho é o mesmo dos outros serviços: /client/payments/initiate com
      // tipoServico DNT + o id do donativo, que o backend liga ao donativo e
      // confirma sem passar pelo callback.
      const porSaldo = WALLET_PAY_ENABLED && walletCardId === 'wallet';
      const porCartaoGuardado = !!walletCardId && walletCardId !== 'sisp' && !porSaldo;
      if (porSaldo || porCartaoGuardado) {
        const pago = await paymentService.initiatePayment({
          serviceId: res.id,
          serviceType: 'DNT',
          amount,
          reference: `DNT-${res.id.substring(0, 10)}`,
          ...(porSaldo ? { paymentMethod: 'wallet' } : { walletCardId }),
          descricao: `Donativo - ${selectedCampaign?.TITULO || 'iFundHere'}`,
        });

        if (!pago.success) {
          throw new Error(pago.error || 'Pagamento com cartão guardado não autorizado');
        }

        setModalOpen(false);
        await loadCampaigns();
        navigate(`/payments/result?origem=doacao&status=success&ref=${pago.merchantRef || pago.transactionId || ''}`);
        return;
      }

      // 2b. Cartão novo: página da SISP (endpoint público, não requer autenticação)
      const payment = await donationsService.initiateSispPayment(
        res.id,
        `${window.location.origin}/payments/result?origem=doacao`,
      );

      if (payment.paymentUrl) {
        // 3. Abrir SISP 3DS em popup por cima da aplicação
        setModalOpen(false);
        const popup = window.open(
          payment.paymentUrl,
          'pagali_sisp_payment',
          'width=900,height=680,scrollbars=yes,resizable=yes,left=160,top=80'
        );
        if (!popup || popup.closed) {
          // Fallback se popup bloqueado pelo browser
          window.location.href = payment.paymentUrl;
        }
      } else {
        throw new Error('Não foi possível iniciar o pagamento SISP');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao processar donativo. Tente novamente.');
    }
  };

  const handleSearch = useCallback((e: React.FormEvent) => { e.preventDefault(); setPage(1); loadCampaigns(); }, [searchQuery, selectedCategory]);

  const totalArrecadado = campaigns.reduce((s, c) => s + Number(c.VALOR_ARRECADADO || 0), 0);
  const totalDoadores   = campaigns.reduce((s, c) => s + Number(c.TOTAL_DOADORES || 0), 0);

  if (loading) return <LoaderComponent size="lg" text="A carregar campanhas..." />;
  if (error) return (
    <div className="text-center py-12">
      <p className="text-red-500 mb-4">{error}</p>
      <button onClick={() => window.location.reload()} className="btn-primary">Tentar novamente</button>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-pink-500 via-rose-500 to-red-500 p-8 text-white">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 70% 50%, white 0%, transparent 60%)' }} />
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <FiHeart className="w-5 h-5 text-pink-200" />
            <span className="text-pink-200 text-sm font-medium">iFundHere by Pagali</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">Doações</h1>
          <p className="text-pink-100 text-sm max-w-md">
            Apoie causas que fazem a diferença em Cabo Verde. Cada donativo conta.
          </p>

          {/* Search bar inside hero */}
          <form onSubmit={handleSearch} className="mt-5 relative max-w-md">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Pesquisar campanhas..."
              className="w-full pl-11 pr-4 py-3 bg-white text-gray-800 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-white/50 shadow-lg placeholder-gray-400"
            />
          </form>
        </div>
      </div>

      {/* Stats bar */}
      {campaigns.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: <FiHeart className="text-pink-500 w-5 h-5" />, bg: 'bg-pink-50', label: 'Campanhas', value: campaigns.length },
            { icon: <FiTarget className="text-emerald-500 w-5 h-5" />, bg: 'bg-emerald-50', label: 'Arrecadado', value: donationsService.formatCurrency(totalArrecadado) },
            { icon: <FiUsers className="text-blue-500 w-5 h-5" />, bg: 'bg-blue-50', label: 'Doadores', value: totalDoadores },
            { icon: <FiStar className="text-amber-500 w-5 h-5" />, bg: 'bg-amber-50', label: 'Em Destaque', value: featuredCampaigns.length },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-2xl p-4 border border-gray-100 flex items-center gap-3">
              <div className={`w-10 h-10 ${s.bg} rounded-xl flex items-center justify-center shrink-0`}>{s.icon}</div>
              <div>
                <p className="text-xs text-gray-400">{s.label}</p>
                <p className="text-lg font-bold text-gray-800">{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CTA — Campanha em Destaque */}
      {featuredCampaigns.length > 0 && !searchQuery && !selectedCategory && (
        <CampanhaDestaqueCTA
          campaign={featuredCampaigns[0]}
          onDoar={() => handleCampaignClick(featuredCampaigns[0])}
        />
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
          <CategoryChip category={{ ID: '', NOME: 'Todas', totalCampanhas: campaigns.length }} selected={!selectedCategory} onClick={() => { setSelectedCategory(null); setPage(1); }} />
          {categories.map(cat => (
            <CategoryChip key={cat.ID} category={cat} selected={selectedCategory === (cat.CODIGO || String(cat.ID))} onClick={() => { setSelectedCategory(cat.CODIGO || String(cat.ID)); setPage(1); }} />
          ))}
        </div>
      )}

      {/* Featured Campaigns */}
      {featuredCampaigns.length > 0 && !searchQuery && !selectedCategory && (
        <div>
          <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FiStar className="text-amber-500 w-4 h-4" /> Campanhas em Destaque
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {featuredCampaigns.slice(0, 3).map((c, i) => (
              <CampaignCard key={c.ID} campaign={c} onClick={() => handleCampaignClick(c)} featured={i === 0} />
            ))}
          </div>
        </div>
      )}

      {/* All Campaigns */}
      <div>
        <h3 className="text-base font-bold text-gray-800 mb-4">
          {searchQuery || selectedCategory ? 'Resultados da pesquisa' : 'Todas as Campanhas'}
        </h3>
        {campaigns.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {campaigns
                .filter(c => !(!searchQuery && !selectedCategory && featuredCampaigns.slice(0, 3).some(f => f.ID === c.ID)))
                .map(c => (
                  <CampaignCard key={c.ID} campaign={c} onClick={() => handleCampaignClick(c)} />
                ))}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 pt-6">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-2.5 rounded-xl border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors cursor-pointer">
                  <FiChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-gray-500 font-medium">Página {page} de {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-2.5 rounded-xl border border-gray-200 disabled:opacity-40 hover:bg-gray-50 transition-colors cursor-pointer">
                  <FiChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        ) : (
          <Card>
            <EmptyState icon={<FiHeart className="text-5xl text-pink-300" />} title="Sem campanhas"
              description={searchQuery || selectedCategory ? 'Nenhuma campanha encontrada com os filtros seleccionados.' : 'Não há campanhas de donativo disponíveis de momento.'} />
          </Card>
        )}
      </div>

      {/* Partner Organizations — no final */}
      {partners.length > 0 && !searchQuery && !selectedCategory && (
        <div className="border-t border-gray-100 pt-6">
          <h3 className="text-base font-bold text-gray-800 mb-4 flex items-center gap-2">
            <FiHeart className="text-pink-500 w-4 h-4" /> Organizações Parceiras
          </h3>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5">
            {partners.map((p: any) => {
              const name = p.nome || p.entidade || '';
              const id   = p.id || p.ID || '';
              const logoStr = typeof p.logo === 'string' ? p.logo : '';
              const mime = p.mimeType || 'image/png';
              let decoded = logoStr;
              if (logoStr && !logoStr.startsWith('http') && !logoStr.startsWith('iVBOR') && !logoStr.startsWith('/9j/')) {
                try { decoded = atob(logoStr); } catch { /* */ }
              }
              const imgSrc = decoded.startsWith('http') ? decoded : (decoded ? `data:${mime};base64,${decoded}` : '');
              return (
                <button key={id} onClick={() => navigate(`/payments/${id}/${encodeURIComponent(name)}`)}
                  className="flex flex-col items-center gap-2 p-3 bg-white rounded-2xl border border-gray-100 hover:border-pink-300 hover:shadow-md transition-all cursor-pointer">
                  {imgSrc ? (
                    <img src={imgSrc} alt={name} className="w-10 h-10 object-contain rounded-lg" />
                  ) : (
                    <div className="w-10 h-10 bg-pink-50 rounded-lg flex items-center justify-center">
                      <FiHeart className="text-pink-400 w-5 h-5" />
                    </div>
                  )}
                  <span className="text-xs text-gray-600 text-center font-medium leading-tight line-clamp-2 w-full">{name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <CampaignDetailModal
        campaign={selectedCampaign}
        isOpen={modalOpen}
        walletCards={walletCards}
        onClose={() => { setModalOpen(false); setSelectedCampaign(null); }}
        onDonate={handleDonate}
      />
    </div>
  );
};

export default Doacoes;
